import React, { useState, useEffect, useRef, useCallback } from 'react';
import Square from './Square';
import ChessPiece from '../ChessPiece';
import PromotionDialog from '../PromotionDialog';
import MoveArrow from './MoveArrow';
import MoveHistoryNavigator from './MoveHistoryNavigator';
import ChessService from '../../../services/chess/chessService';
import { ChessPiece as ChessPieceType, PieceType, PieceColor, FEN } from '../../../types/chess';
import { Chess } from 'chess.js';
import { 
  handleGridNavigation, 
  squareToGridCoords, 
  gridCoordsToSquare,
  handleKeyboardShortcuts 
} from '../../../utils/keyboardNavigation';
import { 
  createSquareAriaAttributes, 
  createChessBoardAriaAttributes,
  announceToScreenReader 
} from '../../../utils/accessibilityHelpers';
import styles from './ChessBoard.module.css';
import coordinateStyles from '../CoordinateChallenge/CoordinateChallenge.module.css';

export interface ChessBoardProps {
  /** The size of the board in pixels */
  size?: number;
  /** Optional square to highlight */
  highlightSquare?: string;
  /** Callback when a square is clicked */
  onSquareClick?: (square: string) => void;
  /** Whether to show coordinates labels */
  showCoordinates?: boolean;
  /** FEN string for initial position */
  fen?: FEN;
  /** Allow moving pieces on the board */
  allowMoves?: boolean;
  /** Callback when a move is made */
  onMove?: (from: string, to: string, promotion?: PieceType) => void;
  /** Optional additional CSS class to apply to the board */
  className?: string;
  /** Optional ChessService instance for testing */
  chessService?: ChessService;
  /** Array of previous moves in algebraic notation */
  moveHistory?: string[];
  /** Current position in move history (0 = start, n = after n moves) */
  currentMoveIndex?: number;
  /** Callback for navigating through the move history */
  onNavigateToMove?: (index: number) => void;
  /** Whether to highlight the last played move */
  highlightLastMove?: boolean;
  /** Enable keyboard navigation for the chess board */
  enableKeyboardNavigation?: boolean;
  /** Initial square to focus when keyboard navigation is enabled */
  initialFocusSquare?: string;
  /** Callback when keyboard shortcuts are used */
  onKeyboardShortcut?: (shortcut: string) => void;
}

/**
 * Helper function to validate and fix FEN strings
 * @param fen FEN string to validate
 * @returns Fixed FEN string if needed
 */
const validateFEN = (fen?: string): string | undefined => {
  if (!fen) return undefined;
  
  try {
    const parts = fen.split(' ');
    if (parts.length !== 6) return fen; // Not a standard FEN format
    
    // Check and fix the en-passant square (4th component)
    const enPassantIndex = 3;
    const enPassant = parts[enPassantIndex];
    
    // If it's not "-" and not a valid square (a-h)(1-8), replace with "-"
    if (enPassant !== '-' && !/^[a-h][1-8]$/.test(enPassant)) {
      // Invalid en-passant square, replace with "-"
      parts[enPassantIndex] = '-';
      return parts.join(' ');
    }
    
    return fen;
  } catch (error) {
    console.error('Error validating FEN:', error);
    return undefined;
  }
};

/**
 * ChessBoard - Renders a standard 8x8 chess board with clickable squares and pieces
 * 
 * This component handles the visual representation of a chess board,
 * piece movement, and dispatches move events to the parent component.
 */
const ChessBoard: React.FC<ChessBoardProps> = ({
  size = 400,
  highlightSquare,
  onSquareClick,
  showCoordinates = true,
  fen,
  allowMoves = true,
  onMove,
  className,
  chessService: initialChessService,
  moveHistory,
  currentMoveIndex = 0,
  onNavigateToMove,
  highlightLastMove = false,
  enableKeyboardNavigation = false,
  initialFocusSquare = 'e4',
  onKeyboardShortcut,
}) => {
  // State for tracking the chess position and user interaction
  const [chessService] = useState(() => initialChessService || new ChessService(new Chess(validateFEN(fen))));
  const [pieces, setPieces] = useState<ChessPieceType[]>([]);
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);
  const [legalMoves, setLegalMoves] = useState<string[]>([]);

  // State for pawn promotion
  const [showPromotionDialog, setShowPromotionDialog] = useState<boolean>(false);
  const [promotionMove, setPromotionMove] = useState<{ from: string; to: string; color: PieceColor } | null>(null);

  // Parse the highlightSquare string to get the square id and feedback type
  // Format: 'e4:correct' or 'a1:incorrect'
  const [highlightedSquare, setHighlightedSquare] = useState<string | undefined>(undefined);
  const [highlightType, setHighlightType] = useState<'correct' | 'incorrect' | undefined>(undefined);

  // Track the last move played (for arrow visualization)
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
  const internalMoveAppliedRef = useRef<boolean>(false);

  // Keyboard navigation state
  const [focusedSquare, setFocusedSquare] = useState<string>(initialFocusSquare);
  const [keyboardNavigationActive, setKeyboardNavigationActive] = useState<boolean>(false);
  const boardRef = useRef<HTMLDivElement>(null);

  // Parse the highlightSquare prop
  useEffect(() => {
    if (!highlightSquare) {
      setHighlightedSquare(undefined);
      setHighlightType(undefined);
      return;
    }

    const parts = highlightSquare.split(':');
    if (parts.length === 2) {
      setHighlightedSquare(parts[0]);
      setHighlightType(parts[1] as 'correct' | 'incorrect');
    } else {
      setHighlightedSquare(highlightSquare);
      setHighlightType(undefined);
    }
  }, [highlightSquare]);

  /**
   * Update the pieces state from the current board position
   */
  const updatePiecesFromBoard = useCallback(() => {
    setPieces(chessService.getPosition());
  }, [chessService]);

  // Update the board when FEN changes
  useEffect(() => {
    if (fen) {
      chessService.resetBoard(validateFEN(fen));
      updatePiecesFromBoard();
      if (internalMoveAppliedRef.current) {
        internalMoveAppliedRef.current = false;
      } else {
        // External board resets (undo/restart/navigation) should clear stale arrows.
        setLastMove(null);
      }
    }
  }, [fen, chessService, updatePiecesFromBoard]);

  // Reset the board state when fen changes
  useEffect(() => {
    setSelectedSquare(null);
    setLegalMoves([]);
    setPromotionMove(null);
    setShowPromotionDialog(false);
    if (enableKeyboardNavigation) {
      setFocusedSquare(initialFocusSquare);
    }
  }, [fen, enableKeyboardNavigation, initialFocusSquare]);

  // Initialize the board position
  useEffect(() => {
    updatePiecesFromBoard();
  }, [updatePiecesFromBoard]);

  /**
   * Check if a move would require pawn promotion
   * @param from Starting square
   * @param to Destination square
   * @returns Whether promotion is required
   */
  const isPromotionMove = useCallback((from: string, to: string): boolean => {
    const piece = chessService.getPiece(from);
    if (!piece || piece.type !== 'p') return false;

    // White pawn reaching the 8th rank or black pawn reaching the 1st rank
    return (
      (piece.color === 'w' && to[1] === '8') ||
      (piece.color === 'b' && to[1] === '1')
    );
  }, [chessService]);

  /**
   * Handle click events on squares and pieces
   * @param square The square that was clicked
   */
  const handleSquareClick = useCallback((square: string) => {
    // Always call the parent's onSquareClick if provided
    if (onSquareClick) {
      onSquareClick(square);
    }

    if (!allowMoves) {
      announceToScreenReader(`Clicked ${square}. Moves not allowed.`);
      return;
    }

    // Get the piece at the clicked square
    const pieceAtSquare = chessService.getPiece(square);

    if (selectedSquare) {
      // If clicking on a legal destination square, handle the move
      if (legalMoves.includes(square)) {
        const pieceBeingMoved = chessService.getPiece(selectedSquare);
        const capturedPiece = chessService.getPiece(square);
        let announcement = `Moving ${pieceBeingMoved?.color === 'w' ? 'white' : 'black'} ${pieceBeingMoved?.type} from ${selectedSquare} to ${square}.`;
        if (capturedPiece) {
          announcement += ` Capturing ${capturedPiece.color === 'w' ? 'white' : 'black'} ${capturedPiece.type}.`;
        }

        // Check if this is a pawn promotion move
        if (isPromotionMove(selectedSquare, square)) {
          // Show promotion dialog instead of making the move immediately
          setPromotionMove({
            from: selectedSquare,
            to: square,
            color: chessService.getPiece(selectedSquare)?.color || 'w'
          });
          setShowPromotionDialog(true);
          announcement += ' Pawn promotion pending.';
        } else {
          // Make a regular move
          const move = chessService.makeMove(selectedSquare, square);

          if (move && onMove) {
            internalMoveAppliedRef.current = true;
            onMove(selectedSquare, square, move.promotion);
            setLastMove({ from: selectedSquare, to: square });
            if (move.promotion) {
              announcement += ` Promoted to ${move.promotion}.`;
            }
            if (chessService.getGameState().inCheck) {
              announcement += ` Check!`;
            } else if (chessService.getGameState().isCheckmate) {
              announcement += ` Checkmate!`;
            } else if (chessService.getGameState().isStalemate) {
              announcement += ` Stalemate!`;
            } else if (chessService.getGameState().isDraw) {
              announcement += ` Draw!`;
            }
          } else {
            announcement = `Illegal move from ${selectedSquare} to ${square}.`;
          }
          updatePiecesFromBoard();
          setSelectedSquare(null);
          setLegalMoves([]);
        }
        announceToScreenReader(announcement);
      }
      // If clicking on the same square, deselect it
      else if (square === selectedSquare) {
        setSelectedSquare(null);
        setLegalMoves([]);
        announceToScreenReader(`Deselected ${square}.`);
      }
      // If clicking on a different piece of the same color, select it instead
      else if (pieceAtSquare && pieceAtSquare.color === chessService.getGameState().turn) {
        setSelectedSquare(square);
        const newLegalMoves = chessService.getLegalMoves(square);
        setLegalMoves(newLegalMoves);
        announceToScreenReader(`Selected ${pieceAtSquare.color === 'w' ? 'white' : 'black'} ${pieceAtSquare.type} on ${square}. ${newLegalMoves.length} legal moves.`);
      } else {
         announceToScreenReader(`Cannot move from ${selectedSquare} to ${square}. Not a legal move.`);
      }
    }
    // If no piece is selected yet and there's a piece of the current turn's color
    else if (pieceAtSquare && pieceAtSquare.color === chessService.getGameState().turn) {
      setSelectedSquare(square);
      const newLegalMoves = chessService.getLegalMoves(square);
      setLegalMoves(newLegalMoves);
      announceToScreenReader(`Selected ${pieceAtSquare.color === 'w' ? 'white' : 'black'} ${pieceAtSquare.type} on ${square}. ${newLegalMoves.length} legal moves.`);
    } else if (pieceAtSquare) {
      announceToScreenReader(`Cannot select ${pieceAtSquare.color === 'w' ? 'white' : 'black'} ${pieceAtSquare.type} on ${square}. It's ${chessService.getGameState().turn === 'w' ? 'white' : 'black'}'s turn.`);
    } else {
      announceToScreenReader(`Clicked empty square ${square}. No piece selected.`);
    }
  }, [onSquareClick, allowMoves, chessService, selectedSquare, legalMoves, onMove, updatePiecesFromBoard, isPromotionMove]);

  // Keyboard navigation handlers
  const handleBoardKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enableKeyboardNavigation) return;

    // Handle keyboard shortcuts first
    const shortcuts = {
      'Escape': () => {
        setSelectedSquare(null);
        setLegalMoves([]);
        announceToScreenReader('Selection cleared. Board focus maintained.');
        onKeyboardShortcut?.('escape');
      },
      'Enter': () => {
        if (focusedSquare) {
          handleSquareClick(focusedSquare);
        }
      },
      ' ': () => {
        if (focusedSquare) {
          handleSquareClick(focusedSquare);
        }
      }
    };

    if (handleKeyboardShortcuts(event, shortcuts)) {
      event.preventDefault();
      return;
    }

    // Handle grid navigation
    const currentCoords = squareToGridCoords(focusedSquare);
    if (!currentCoords) return;

    const newCoords = handleGridNavigation(
      event,
      currentCoords.row,
      currentCoords.col,
      { rows: 8, columns: 8, wrap: true }
    );

    if (newCoords) {
      event.preventDefault();
      const newSquare = gridCoordsToSquare(newCoords.row, newCoords.col);
      if (newSquare) {
        setFocusedSquare(newSquare);

        const pieceOnNewSquare = chessService.getPiece(newSquare);
        const pieceDescription = pieceOnNewSquare
          ? `${pieceOnNewSquare.color === 'w' ? 'White' : 'Black'} ${pieceOnNewSquare.type}`
          : 'empty';
        const isLegalMoveSquare = legalMoves.includes(newSquare);
        const isSelectedSquare = newSquare === selectedSquare;
        announceToScreenReader(`${newSquare}, ${pieceDescription}. ${isLegalMoveSquare ? 'Legal move.' : ''} ${isSelectedSquare ? 'Selected.' : ''}`);
      }
    }
  }, [enableKeyboardNavigation, focusedSquare, chessService, onKeyboardShortcut, legalMoves, selectedSquare, handleSquareClick]);

  // Set up keyboard event listeners
  useEffect(() => {
    if (!enableKeyboardNavigation) return;

    const boardElement = boardRef.current;
    if (!boardElement) return;

    boardElement.addEventListener('keydown', handleBoardKeyDown);
    
    return () => {
      boardElement.removeEventListener('keydown', handleBoardKeyDown);
    };
  }, [handleBoardKeyDown, enableKeyboardNavigation]);

  // Handle keyboard events from individual squares
  const handleSquareKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!enableKeyboardNavigation || !keyboardNavigationActive) return;

    // Convert React keyboard event to regular keyboard event for compatibility
    const nativeEvent = new KeyboardEvent('keydown', {
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      bubbles: true,
      cancelable: true,
    });

    handleBoardKeyDown(nativeEvent);
    if (nativeEvent.defaultPrevented) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [enableKeyboardNavigation, keyboardNavigationActive, handleBoardKeyDown]);

  // Focus management for the board container
  useEffect(() => {
    if (enableKeyboardNavigation && keyboardNavigationActive && boardRef.current) {
      if (!boardRef.current.contains(document.activeElement) || document.activeElement === boardRef.current) {
      }
    }
  }, [enableKeyboardNavigation, keyboardNavigationActive]);

  // Update last move when move history or current index changes
  useEffect(() => {
    if (moveHistory && moveHistory.length > 0 && currentMoveIndex > 0 && currentMoveIndex <= moveHistory.length) {
      // Get the last move from history based on currentMoveIndex
      const moveNotation = moveHistory[currentMoveIndex - 1];
      
      // Parse the move if possible
      try {
        // Create a temporary chess instance to parse the move
        const tempChess = new Chess(chessService.getFen());
        // Make the moves up to the current index to find the last move
        for (let i = 0; i < currentMoveIndex; i++) {
          if (i === currentMoveIndex - 1) {
            const moveObj = tempChess.move(moveHistory[i]);
            if (moveObj) {
              setLastMove({
                from: moveObj.from,
                to: moveObj.to
              });
            }
          } else {
            tempChess.move(moveHistory[i]);
          }
        }
      } catch (error) {
        console.error('Error parsing move:', error);
        setLastMove(null);
      }
    } else {
      setLastMove(null);
    }
  }, [moveHistory, currentMoveIndex, chessService]);



  /**
   * Handle the selection of a promotion piece
   * @param pieceType The selected piece type for promotion
   */
  const handlePromotionSelect = (pieceType: PieceType) => {
    if (!promotionMove) return;

    const { from, to } = promotionMove;
    const move = chessService.makeMove(from, to, pieceType);

    if (move && onMove) {
      internalMoveAppliedRef.current = true;
      onMove(from, to, pieceType);
      
      // Update last move for arrow visualization
      setLastMove({ from, to });
    }

    // Update the pieces on the board
    updatePiecesFromBoard();

    // Reset state
    setShowPromotionDialog(false);
    setPromotionMove(null);
    setSelectedSquare(null);
    setLegalMoves([]);
  };

  /**
   * Handle cancellation of the promotion dialog
   */
  const handlePromotionCancel = () => {
    setShowPromotionDialog(false);
    setPromotionMove(null);
  };



  /**
   * Renders all 64 squares on the chess board with pieces and highlighting
   * @returns An array of Square components
   */
  const renderSquares = () => {
    const squares = [];
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = ['8', '7', '6', '5', '4', '3', '2', '1']; // Inverted to show board correctly

    // Loop through ranks and files to create all squares
    for (let rankIndex = 0; rankIndex < 8; rankIndex++) {
      for (let fileIndex = 0; fileIndex < 8; fileIndex++) {
        const file = files[fileIndex];
        const rank = ranks[rankIndex];
        const squareId = `${file}${rank}`;

        // Determine if this is a light square (alternating pattern)
        // A square is light if the sum of its file and rank indices is even
        const isLight = (fileIndex + rankIndex) % 2 === 1;

        // Check if this square should be highlighted
        const isHighlighted = squareId === highlightedSquare;

        // Check if this square is a legal move for the selected piece
        const isLegalMove = legalMoves.includes(squareId);

        // Check if this square has a piece
        const piece = pieces.find(p => p.square === squareId);

        // Is this the selected square
        const isSelected = squareId === selectedSquare;

        // Is this the keyboard-focused square
        const isFocused = enableKeyboardNavigation && keyboardNavigationActive && squareId === focusedSquare;

        // Create accessibility attributes for the square
        const currentPiece = piece;
        const squareAriaAttrs = createSquareAriaAttributes(
          squareId, 
          currentPiece ? { type: currentPiece.type, color: currentPiece.color } : undefined, 
          isSelected, 
          isHighlighted, 
          isLegalMove
        );
        
        squares.push(
          <Square
            key={squareId}
            id={squareId}
            isLight={isLight}
            isHighlighted={isHighlighted}
            isLegalMove={isLegalMove}
            isSelected={isSelected}
            isFocused={isFocused}
            highlightType={isHighlighted ? highlightType : undefined}
            onClick={() => handleSquareClick(squareId)}
            onKeyDown={enableKeyboardNavigation ? handleSquareKeyDown : undefined}
            showCoordinate={showCoordinates && (rankIndex === 7 || fileIndex === 0)}
            piece={piece}
            role="gridcell"
            tabIndex={isFocused ? 0 : -1}
            aria-label={squareAriaAttrs['aria-label']}
            aria-selected={squareAriaAttrs['aria-selected']}
            aria-current={squareAriaAttrs['aria-current']}
          >
            {piece && (
              <ChessPiece
                type={piece.type}
                color={piece.color}
                square={squareId}
                isSelected={isSelected}
                onClick={() => handleSquareClick(squareId)}
              />
            )}
          </Square>
        );
      }
    }

    return squares;
  };

  /**
   * Renders coordinate labels around the board
   * @returns Element with coordinate labels
   */
  const renderCoordinateLabels = () => {
    if (!showCoordinates) return null;

    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];

    return (
      <div className={styles.coordinateLabels} aria-hidden="true">
        {/* File labels (bottom) */}
        <div className={styles.fileLabels}>
          {files.map(file => (
            <div key={`file-${file}`} className={styles.fileLabel}>{file}</div>
          ))}
        </div>

        {/* Rank labels (left side) */}
        <div className={styles.rankLabels}>
          {ranks.map(rank => (
            <div key={`rank-${rank}`} className={styles.rankLabel}>{rank}</div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renders a move arrow to visualize the last played move
   * @returns MoveArrow component
   */
  const renderMoveArrow = () => {
    if (!highlightLastMove || !lastMove) return null;
    
    return (
      <MoveArrow
        from={lastMove.from}
        to={lastMove.to}
        type="lastMove" 
        boardSize={size}
      />
    );
  };

  // Calculate position for promotion dialog
  const getPromotionDialogPosition = () => {
    // If no promotion move is in progress, return undefined for centered position
    if (!promotionMove) return undefined;

    // Get the coordinates of the target square
    const file = promotionMove.to[0];
    const rank = promotionMove.to[1];

    // Convert to pixel coordinates
    const fileIndex = 'abcdefgh'.indexOf(file);
    const rankIndex = '87654321'.indexOf(rank);

    // Get square size and calculate board dimensions
    const squareSize = size / 8;
    const boardWidth = size;
    const boardHeight = size;

    // Calculate base position (center of the target square)
    let left = fileIndex * squareSize + squareSize / 2;
    let top = rankIndex * squareSize + squareSize / 2;

    // Adjust position to ensure dialog stays within board boundaries
    // If the square is in the right half of the board, shift dialog left
    if (fileIndex > 3) {
      left -= 150; // approximate dialog width / 2
    } else {
      left += 50; // slight offset from center to avoid covering the square
    }

    // If the square is in the bottom half of the board, shift dialog up
    if (rankIndex > 3) {
      top -= 150; // approximate dialog height / 2
    } else {
      top += 50; // slight offset from center
    }

    // Ensure the dialog stays within the board bounds with some padding
    const padding = 10;
    left = Math.max(padding, Math.min(left, boardWidth - 300 - padding));
    top = Math.max(padding, Math.min(top, boardHeight - 300 - padding));

    return { top, left };
  };

  // Create board accessibility attributes
  const boardAriaAttributes = enableKeyboardNavigation 
    ? createChessBoardAriaAttributes(8, 8)
    : { role: "grid", "aria-label": "Chess board" };

  return (
    <div className={`${styles.chessBoardContainer} ${className || ''}`}>
      <div
        ref={boardRef}
        data-testid="chess-board"
        className={`${styles.chessBoard} ${coordinateStyles.spyCamera}`}
        style={{ width: `${size}px`, height: `${size}px` }}
        role={boardAriaAttributes.role}
        aria-label={boardAriaAttributes['aria-label']}
        {...(enableKeyboardNavigation && {
          'aria-rowcount': (boardAriaAttributes as any)['aria-rowcount'],
          'aria-colcount': (boardAriaAttributes as any)['aria-colcount']
        })}
        data-fen={fen}
        tabIndex={enableKeyboardNavigation ? 0 : -1}
        onFocus={() => {
          if (enableKeyboardNavigation) {
            setKeyboardNavigationActive(true);
            if (document.activeElement === boardRef.current) {
              announceToScreenReader("Chess board focused. Use arrow keys to navigate squares.");
            }
          }
        }}
        onBlur={() => {
          if (enableKeyboardNavigation) {
          }
        }}
      >
        {renderSquares()}
        {renderMoveArrow()}
      </div>
      {renderCoordinateLabels()}

      {/* Move History Navigator */}
      {moveHistory && onNavigateToMove && (
        <MoveHistoryNavigator
          moveHistory={moveHistory}
          currentMoveIndex={currentMoveIndex}
          onNavigateToMove={onNavigateToMove}
        />
      )}

      {/* Promotion Dialog */}
      {showPromotionDialog && promotionMove && (
        <PromotionDialog
          color={promotionMove.color}
          onSelect={handlePromotionSelect}
          onCancel={handlePromotionCancel}
          position={getPromotionDialogPosition()}
        />
      )}
    </div>
  );
};

export default ChessBoard;
